import React, { useMemo, useState } from "react";
import ReactPlayer from "react-player";
import { WarningIcon } from "@picklebet/ui";
import { LoaderIcon } from "@picklebet/ui";
import type { Stream } from "../stores/streamStore";
import { isDisplayMode } from "utilities/display";
import { telemetry } from "@/utilities/telemetry";
import { useBettingEvent } from "hooks/firestore/betting/useEvent";

import * as styles from "./StreamPlayerWithLoader.module.scss";

const PICKLE_DOMAINS = ["skrilla.com", "picklebet.com", "localhost"];

const MATCH_URL_TWITCH_VIDEO = /(?:www\.|go\.)?twitch\.tv\/videos\/(\d+)($|\?)/;
const MATCH_URL_TWITCH_CHANNEL =
  /(?:www\.|go\.)?twitch\.tv\/([a-zA-Z0-9_]+)($|\?)/;

export const isTwitchStream = (url: string) => {
  if (!url) return false;
  return url.indexOf("twitch.tv") >= 0;
};

export const isBetMakersStream = (url: string) => {
  if (!url) return false;
  return url.indexOf("thebetmakers.com") >= 0;
};

export const IFrameStream = ({
  streamUrl,
  onLoad,
}: {
  streamUrl: string;
  onLoad?: () => void;
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  let url = streamUrl;

  if (isTwitchStream(streamUrl)) {
    const isChannel = MATCH_URL_TWITCH_CHANNEL.test(streamUrl);
    const query = isChannel
      ? `channel=${streamUrl.match(MATCH_URL_TWITCH_CHANNEL)?.[1]}`
      : `video=${streamUrl.match(MATCH_URL_TWITCH_VIDEO)?.[1]}`;
    url = `https://player.twitch.tv/?${query}&parent=${location.hostname}&muted=true&autoplay=true&theme=dark`;
  }

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    onLoad?.();
  };

  return (
    <iframe
      src={url}
      width="100%"
      height="100%"
      allowFullScreen={true}
      onLoad={handleIframeLoad}
      style={{ display: iframeLoaded ? "block" : "none", border: "none" }}
    />
  );
};

const DEFAULT_VOLUME = 0.5;

const STREAM_WAITING = "/liveStream-holding-compressed.mp4";

export const isPickleStream = (url: string) => {
  if (!url) return true;
  return PICKLE_DOMAINS.some((domain) => {
    return url?.indexOf(domain) >= 0;
  });
};

export const StreamPlayerWithLoader = ({ stream }: { stream: Stream }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [attemptedLoadCount, setAttemptedLoadCount] = useState(0);
  // Only the event doc is needed for streamUri — no markets/competitors.
  const { event, isLoading: loading } = useBettingEvent(stream?.eventId);
  const url = stream.url ?? event?.streamUri ?? STREAM_WAITING;
  const canPlay = useMemo(
    () =>
      isTwitchStream(url) ||
      isBetMakersStream(url) ||
      ReactPlayer.canPlay?.(url) ||
      false,
    [url, ReactPlayer.canPlay],
  );

  const streamError = (error?: any) => {
    telemetry.streamError({
      eventId: stream.eventId,
      url: stream.url ?? "",
      title: stream.title ?? "",
      sport: stream.sport,
      error: error?.toString(),
    });
    setIsLoading(true);
    setAttemptedLoadCount(attemptedLoadCount + 1);
  };

  const ready = () => {
    setAttemptedLoadCount(0);
    setIsLoading(false);
  };

  const reset = () => {
    setAttemptedLoadCount(0);
    setIsLoading(true);
  };

  const mustLoop = url === STREAM_WAITING;

  const renderStreamError = () => {
    return (
      <div className={styles.streamError}>
        <div className={styles.errorIcon}>
          <WarningIcon />
        </div>
        <div className={styles.errorMessage}>
          There was an error loading the stream, or the stream has finished.
        </div>
        <div className={styles.streamErrorButton} onClick={reset}>
          Try again
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <div className={styles.loading}>
        <div>
          <LoaderIcon />
        </div>
      </div>
    );
  };

  return (
    <div key={attemptedLoadCount} className={styles.stream}>
      {canPlay && attemptedLoadCount < 4 ? (
        <>
          {isLoading && !loading && renderLoading()}
          {isTwitchStream(url) || isBetMakersStream(url) ? (
            <IFrameStream streamUrl={url} onLoad={ready} />
          ) : (
            <ReactPlayer
              key={stream.eventId}
              src={url}
              width={`100%`}
              height={`100%`}
              muted={stream.muted}
              volume={DEFAULT_VOLUME}
              playing={true}
              playsInline={true}
              loop={mustLoop}
              controls={isDisplayMode("mobile") ? true : !isPickleStream(url)}
              onReady={ready}
              onError={streamError}
              style={{
                display: isLoading ? "none" : "block",
              }}
            />
          )}
        </>
      ) : (
        renderStreamError()
      )}
    </div>
  );
};
